import { Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  Box,
  Icons,
  MetricsBox,
  RatioProgressBar,
  Stack,
  tokens,
  Tooltip,
  Typography,
} from '@cast/design-system';
import { LogoSmallSvg } from '@cast/features-cluster-ui';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { CollectingDataMessage } from '@cast/split-me/components';
import {
  makeEditScalingPolicySettingsLink,
  makeScalingPoliciesLink,
} from '@cast/split-me/features/cluster/workload-optimization';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { CardCurrentDate } from '../_components/CardCurrentDate';
import { InventoryListItem } from '../../../_components/ui/InventoryListItem/InventoryListItem';
import { DashboardMetricSummaryCardSkeleton } from '../_components/DashboardCardSkeletons';
import { SCALING_POLICY_CATEGORY_COLOR_LIST } from './_components/scalingPoliciesCategoryColors';
import { ScalingPoliciesTooltip } from './_components/ScalingPoliciesTooltip';
import {
  ScalingPoliciesCardItem,
  TOP_SCALING_POLICIES_LIMIT,
  useScalingPoliciesCardData,
} from './_hooks/useScalingPoliciesCardData';

const CARD_TITLE = 'Scaling policies';

const PROGRESS_BAR_HEIGHT = 8;
const PROGRESS_BAR_BORDER_RADIUS = PROGRESS_BAR_HEIGHT / 2;

const POLICY_ICON_SIZE = 14;

const PolicyMarker = ({ item }: { item: ScalingPoliciesCardItem }) => {
  if (item.isSystemPolicy) {
    return (
      <LogoSmallSvg
        height={POLICY_ICON_SIZE}
        width={POLICY_ICON_SIZE}
        color={tokens.theme.fg.action.primary}
      />
    );
  }

  if (item.isOpsPilot) {
    return (
      <Icons.Sparkle
        size={POLICY_ICON_SIZE}
        color={tokens.theme.fg.action.primary}
      />
    );
  }

  return null;
};

const ScalingPoliciesCardSkeleton = () => (
  <DashboardMetricSummaryCardSkeleton
    metricTestId="scaling-policies-total-skeleton"
    listCount={TOP_SCALING_POLICIES_LIMIT}
    progressBarHeight={PROGRESS_BAR_HEIGHT}
  />
);

export const ScalingPoliciesCard = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching, hasError, refetch } =
    useScalingPoliciesCardData();

  const isCardLoading = isLoading || (hasError && isFetching);
  const scalingPoliciesLink = makeScalingPoliciesLink();

  return (
    <WidgetLayout
      title={CARD_TITLE}
      testId="scaling-policies-card"
      headerEndContent={<CardCurrentDate />}
    >
      {isCardLoading ? (
        <ScalingPoliciesCardSkeleton />
      ) : hasError ? (
        <FailedToLoad
          title="Failed to load scaling policies"
          refresh={refetch}
        />
      ) : data && data.total > 0 ? (
        <Stack height="100%" gap={24} justifyContent="space-between">
          <Stack
            gap={24}
            data-testid="scaling-policies-card-summary"
            sx={{ flexShrink: 0 }}
          >
            <MetricsBox
              testId="scaling-policies-total"
              size="m"
              slots={{
                label: 'Total policies',
                value: (
                  <Typography
                    component={RouterLink}
                    to={scalingPoliciesLink}
                    variant="metricmd"
                    color={tokens.theme.fg.action.primary}
                    sx={{ textDecoration: 'none' }}
                  >
                    {data.total}
                  </Typography>
                ),
              }}
              slotProps={{
                label: { variant: 'L12M', color: 'textSecondary' },
              }}
            />

            <Tooltip
              title={<ScalingPoliciesTooltip categories={data.categories} />}
              placement="top"
              sx={{
                maxWidth: 'none',
                backgroundColor: tokens.theme.bg.structural.surface,
                color: tokens.theme.fg.structural.primary,
                boxShadow: tokens.theme.shadows.elevation.medium,
              }}
              PopperProps={{
                sx: {
                  '& .MuiTooltip-arrow': {
                    color: tokens.theme.bg.structural.surface,
                  },
                },
              }}
            >
              <Box sx={{ width: '100%' }}>
                <RatioProgressBar
                  testId="scaling-policies-bar"
                  bars={data.categories.map((category, index) => ({
                    value: category.count,
                    color: SCALING_POLICY_CATEGORY_COLOR_LIST[index],
                  }))}
                  hideBorders
                  sx={{
                    height: PROGRESS_BAR_HEIGHT,
                    minHeight: PROGRESS_BAR_HEIGHT,
                    borderRadius: PROGRESS_BAR_BORDER_RADIUS,
                    flexShrink: 0,
                  }}
                />
              </Box>
            </Tooltip>
          </Stack>

          <Stack
            gap={0}
            flex="0 1 auto"
            minHeight={0}
            overflow="auto"
            data-testid="scaling-policies-card-details"
          >
            {data.items.map((item, index) => (
              <InventoryListItem
                key={item.key}
                size="sm"
                divider={index < data.items.length - 1}
                onClick={() =>
                  navigate(
                    makeEditScalingPolicySettingsLink({ policyId: item.key })
                  )
                }
                slots={{
                  startContent: <PolicyMarker item={item} />,
                  label: item.label,
                  endContent: item.value,
                }}
                slotProps={{
                  label: {
                    variant: 'P12M',
                    color: tokens.theme.fg.action.primary,
                  },
                  endContent: { variant: 'P12B' },
                }}
              />
            ))}
          </Stack>
        </Stack>
      ) : (
        <CollectingDataMessage
          title="Collecting data"
          description="Data about workload optimization is being collected. The page will be populated as soon as possible."
          sx={{ py: 24 }}
        />
      )}
    </WidgetLayout>
  );
};
